import React from 'react';
import styled, { css } from 'styled-components';
import TinyColor from 'tinycolor2';

import {
  GqlProductCardDisplayOptions,
  MobileAppFlair,
  ProductCardButtonOption,
  ProductCardPillDisplay,
  ProductCardTextAlignmentOption,
} from 'types/graphql';

import {
  DEFAULT_STRAIN_TYPE_PILL_BACKGROUND,
  getStrainTypePillBackground,
  getStrainTypePillTextColor,
  StrainTypeColors,
} from '@dutchie/common/utils/strain-type-colors';

import Imgix from 'shared/components/imgix';
import { ImgixDefaultSettings } from 'shared/constants';
import { formatCurrency } from 'shared/helpers/utils';

import { StarCheck } from 'src/svg/star-check';
import { DiscountTag } from 'src/svg/discount-tag';
import SvgAddIcon from 'src/dispensary-admin/customize/components/preview/home-page/carousels/product-carousel/product-carousel-card/svg/add-icon';
import { getButtonClipPath } from 'src/dispensary-admin/customize/components/preview/home-page/get-flair-path';

/** One mobile app card's display copy, already formatted -- this component does no product lookup. */
export type MobileAppPreviewProduct = {
  name: string;
  brandName: string;
  imageUrl: string;
  strainType: string;
  potency: string[];
  effects: string;
  discountName: string;
  collection: string;
  price: number;
  weight: string;
  originalPrice?: number;
  discountLabel?: string;
  isStaffPick?: boolean;
};

type MobileAppProductCardProps = {
  product: MobileAppPreviewProduct;
  displayOptions?: GqlProductCardDisplayOptions | null;
  strainTypes?: StrainTypeColors | null;
  textAlignment?: ProductCardTextAlignmentOption | null;
  buttonFlair?: MobileAppFlair;
};

const imgixParams = {
  ...ImgixDefaultSettings,
  h: 188,
  w: 188,
  fit: 'max',
};

// `fallbackBackground` must match whatever color is actually painted behind the text when `color`
// is unset, or the lightness check runs against a color that isn't rendered.
function getContrastTextColor(
  color: string | null | undefined,
  fallbackDark = '#2c3236',
  fallbackBackground = fallbackDark
): string {
  return TinyColor(color ?? fallbackBackground).isLight() ? fallbackDark : '#fff';
}

export const MobileAppProductCard = ({
  product,
  displayOptions,
  strainTypes,
  textAlignment,
  buttonFlair,
}: MobileAppProductCardProps): JSX.Element => {
  const {
    backgroundColor,
    brandColor,
    buttonDisplay,
    potencyColor,
    potencyDisplay,
    priceColor,
    productNameColor,
    strainTypeDisplay,
    strikethroughPriceColor,
    weightColor,
  } = displayOptions ?? {};

  // Every row is gated on its option *and* on actually having something to show -- real products
  // routinely lack a strain type, effects or a collection, and an enabled option shouldn't leave
  // an empty row (or a lone collection diamond) behind.
  const showStrain = (displayOptions?.showStrainType ?? true) && !!product.strainType;
  const showPotency = (displayOptions?.showPotencyDetails ?? true) && product.potency.length > 0;
  const showEffects = (displayOptions?.showEffects ?? true) && !!product.effects;
  const showDiscountName = (displayOptions?.showDiscountNames ?? true) && !!product.discountName;
  const showCollection = (displayOptions?.showCollection ?? true) && !!product.collection;
  const showWeight = (displayOptions?.showProductWeight ?? false) && !!product.weight;

  // Only one tag sits over the image, and a discount always wins over Staff Pick. The discount
  // badge itself isn't gated on showDiscountNames -- that option governs the discount *name* row
  // further down the card, not the "% off" badge, which the app shows for any discounted product.
  const hasDiscountBadge = !!product.discountLabel;
  const showStaffPick = !hasDiscountBadge && !!product.isStaffPick && (displayOptions?.showStaffPick ?? true);

  const strainBackground = getStrainTypePillBackground(product.strainType, strainTypes);
  const strainVariant = strainTypeDisplay ?? ProductCardPillDisplay.filled;
  const isPotencyBoxed =
    potencyDisplay === ProductCardPillDisplay.border || potencyDisplay === ProductCardPillDisplay.filled;
  const isCentered = textAlignment === ProductCardTextAlignmentOption.center;
  const buttonStyle = buttonDisplay ?? ProductCardButtonOption.icon;
  const useLargeButton = buttonStyle !== ProductCardButtonOption.icon;

  return (
    <Card $backgroundColor={backgroundColor} $isCentered={isCentered}>
      {hasDiscountBadge && <DiscountBadge>{product.discountLabel}</DiscountBadge>}

      {showStaffPick && (
        <StaffPickBadge>
          <StarCheck />
          Staff Pick
        </StaffPickBadge>
      )}

      <div>
        <ImageWrapper>
          <Image src={product.imageUrl} imgixParams={imgixParams} />
        </ImageWrapper>

        <Name $color={productNameColor}>{product.name}</Name>
        <Brand $color={brandColor}>{product.brandName}</Brand>
      </div>

      <DetailsGroup>
        {(showStrain || showPotency) && (
          <Details>
            {showStrain && (
              <Strain $color={strainBackground} $variant={strainVariant}>
                {product.strainType}
              </Strain>
            )}

            {showPotency &&
              (isPotencyBoxed ? (
                product.potency.map((value) => (
                  <PotencyBox key={value} $variant={potencyDisplay} $color={potencyColor}>
                    {value}
                  </PotencyBox>
                ))
              ) : (
                <Potency $color={potencyColor}>{product.potency.join(' · ')}</Potency>
              ))}
          </Details>
        )}

        {showEffects && <Effects>{product.effects}</Effects>}
      </DetailsGroup>

      <FooterGroup>
        {showDiscountName && (
          <DiscountName>
            <DiscountTag width={12} height={12} />
            <DiscountNameText>{product.discountName}</DiscountNameText>
          </DiscountName>
        )}

        {showCollection && <Collection>{product.collection}</Collection>}

        <PriceRow $useLargeButton={useLargeButton}>
          <PriceDetails>
            <Price $color={priceColor}>{formatCurrency(product.price)}</Price>

            {product.originalPrice !== undefined && (
              <OriginalPrice $color={strikethroughPriceColor}>{formatCurrency(product.originalPrice)}</OriginalPrice>
            )}

            {showWeight && <Weight $color={weightColor}>{product.weight}</Weight>}
          </PriceDetails>

          {!useLargeButton && (
            <AddButton type='button' aria-label='Add to cart' $buttonFlair={buttonFlair}>
              <SvgAddIcon />
            </AddButton>
          )}
        </PriceRow>

        {useLargeButton && (
          <LargeAddButton type='button' $variant={buttonStyle}>
            Add to Cart
          </LargeAddButton>
        )}
      </FooterGroup>
    </Card>
  );
};

// Three groups under `space-between`: the card stretches to the tallest card in its row and the
// slack lands where the app puts it (below the brand and above the price), rather than being
// reserved up front by fixed heights -- nothing here sets a height or min-height.
const Card = styled.div<{ $backgroundColor?: string | null; $isCentered: boolean }>`
  --text-alignment: ${({ $isCentered }) => ($isCentered ? 'center' : 'left')};
  --content-justify: ${({ $isCentered }) => ($isCentered ? 'center' : 'flex-start')};
  // Centered cards tuck the add button up against the price instead of pushing it to the far
  // edge, so the price and button read as one centered group.
  --price-justify: ${({ $isCentered }) => ($isCentered ? 'center' : 'space-between')};

  background: ${({ $backgroundColor, theme }) => $backgroundColor ?? (theme?.isBackgroundDark ? 'none' : '#fff')};
  border: 1px solid ${({ theme }) => (theme?.isBackgroundDark ? '#6e6e6e' : '#e3e7e9')};
  border-radius: ${({ theme }) => theme?.radius?.lg};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px;
  position: relative;
  text-align: var(--text-alignment);
  width: 100%;
`;

// Anchored to the card's corners rather than the padded image box -- on the app these badges sit
// flush in the corner and overlap a full-bleed product image.
const badgeStyles = css`
  align-items: center;
  display: flex;
  gap: 4px;
  line-height: 1;
  position: absolute;
  text-transform: ${({ theme }) => theme?.textTransforms?.tags};
  top: 0;
  z-index: 1;
`;

const DiscountBadge = styled.div`
  ${badgeStyles}
  background: ${({ theme }) => theme?.discountTagColor};
  border-radius: ${({ theme }) => theme?.radius?.sm};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  top: 8px;
  left: 8px;
  padding: 6px 8px;
`;

const StaffPickBadge = styled.div`
  ${badgeStyles}
  color: ${({ theme }) => theme?.staffPickTagColor};
  font-size: 13px;
  font-weight: 600;
  padding: 6px 8px;
  right: 0;
`;

const ImageWrapper = styled.div`
  align-items: center;
  aspect-ratio: 1 / 1;
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  width: 100%;

  img {
    display: block;
    max-height: 100%;
    max-width: 100%;
  }
`;

const Image = styled(Imgix)<{ src: string; imgixParams: Record<string, number | string> }>`
  display: block;
  margin: 0 auto;
  max-height: 100%;
  max-width: 100%;
`;

// Falls back to the dark-mode (inverted) ink only when no explicit color override is set and the
// app background is too dark for the light-mode default to meet WCAG AA -- see ECOMM-12580.
const Name = styled.div<{ $color?: string | null }>`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ $color, theme }) => $color ?? (theme?.isBackgroundDark ? '#ffffff' : '#121516')};
  display: -webkit-box;
  font-size: 16px;
  font-weight: 600;
  line-height: ${20 / 16};
  overflow: hidden;
`;

const Brand = styled.div<{ $color?: string | null }>`
  color: ${({ $color, theme }) => $color ?? (theme?.isBackgroundDark ? '#c7ccd1' : '#646d72')};
  font: 14px / ${18 / 14} ${({ theme }) => theme?.secondaryFont};
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Group = styled.div`
  > *:first-child {
    margin-top: 0;
  }
`;

const DetailsGroup = styled(Group)`
  margin-top: 12px;
`;

const FooterGroup = styled(Group)`
  margin-top: 10px;
`;

// Potency flows inline after the strain pill and wraps beneath it, as on the app -- so both are
// inline-level rather than stacked blocks.
const Details = styled.div`
  line-height: 20px;
`;

const Strain = styled.span<{ $color?: string | null; $variant: ProductCardPillDisplay }>`
  background: ${({ $variant, $color }) =>
    $variant === ProductCardPillDisplay.filled ? $color ?? DEFAULT_STRAIN_TYPE_PILL_BACKGROUND : 'transparent'};
  border: ${({ $variant, $color }) =>
    $variant === ProductCardPillDisplay.border ? `1px solid ${$color ?? '#2c3236'}` : 'none'};
  border-radius: ${({ $variant }) => ($variant === ProductCardPillDisplay.text ? '0' : '12px')};
  color: ${({ $variant, $color }) => {
    if ($variant === ProductCardPillDisplay.filled) {
      // Must resolve against the same fallback the `background` above paints, or the contrast
      // check runs against a color that isn't actually rendered.
      return getStrainTypePillTextColor($color ?? DEFAULT_STRAIN_TYPE_PILL_BACKGROUND);
    }

    return $color ?? '#2c3236';
  }};
  display: inline-block;
  font: 500 13px/1 ${({ theme }) => theme?.secondaryFont};
  margin-right: 6px;
  padding: ${({ $variant }) => ($variant === ProductCardPillDisplay.text ? '0' : '5px 10px')};
  vertical-align: middle;
`;

const Potency = styled.span<{ $color?: string | null }>`
  color: ${({ $color, theme }) => $color ?? (theme?.isBackgroundDark ? '#c7ccd1' : '#646d72')};
  font: 13px / ${20 / 13} ${({ theme }) => theme?.secondaryFont};
  vertical-align: middle;
`;

const PotencyBox = styled.span<{ $variant: ProductCardPillDisplay; $color?: string | null }>`
  background: ${({ $variant, $color }) =>
    $variant === ProductCardPillDisplay.filled ? $color ?? '#646d72' : 'transparent'};
  border: ${({ $variant, $color }) =>
    $variant === ProductCardPillDisplay.border ? `1px solid ${$color ?? '#646d72'}` : 'none'};
  border-radius: ${({ theme }) => theme?.radius?.tags};
  color: ${({ $variant, $color }) =>
    $variant === ProductCardPillDisplay.border
      ? $color ?? '#646d72'
      : getContrastTextColor($color, '#2c3236', '#646d72')};
  display: inline-block;
  font: 13px/1 ${({ theme }) => theme?.secondaryFont};
  margin: 0 4px 4px 0;
  padding: 4px 6px;
  vertical-align: middle;
`;

const Effects = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ theme }) => (theme?.isBackgroundDark ? '#c7ccd1' : '#646d72')};
  display: -webkit-box;
  font: 13px / ${18 / 13} ${({ theme }) => theme?.secondaryFont};
  margin-top: 6px;
  overflow: hidden;
`;

const DiscountName = styled.div`
  align-items: center;
  color: ${({ theme }) => theme?.discountTagColor};
  display: flex;
  font-size: 13px;
  font-weight: 500;
  gap: 4px;
  justify-content: var(--content-justify);
  line-height: ${18 / 13};
  text-transform: ${({ theme }) => theme?.textTransforms?.tags};

  svg {
    flex-shrink: 0;
  }
`;

const DiscountNameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Collection = styled.div`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: ${({ theme }) => (theme?.isBackgroundDark ? '#c7ccd1' : '#646d72')};
  display: -webkit-box;
  font-size: 13px;
  font-weight: 500;
  line-height: ${18 / 13};
  margin-top: 6px;
  overflow: hidden;

  &::before {
    background: currentColor;
    content: '';
    display: inline-block;
    height: 6px;
    margin-right: 6px;
    transform: rotate(45deg);
    vertical-align: middle;
    width: 6px;
  }
`;

const PriceRow = styled.div<{ $useLargeButton: boolean }>`
  align-items: flex-end;
  display: flex;
  gap: 8px;
  justify-content: ${({ $useLargeButton }) => ($useLargeButton ? 'var(--content-justify)' : 'var(--price-justify)')};
  margin-top: 10px;
`;

const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const Price = styled.div<{ $color?: string | null }>`
  color: ${({ $color, theme }) => $color ?? (theme?.isBackgroundDark ? '#ffffff' : '#2c3236')};
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
`;

const OriginalPrice = styled.div<{ $color?: string | null }>`
  color: ${({ $color, theme }) => $color ?? (theme?.isBackgroundDark ? '#c7ccd1' : '#485055')};
  font-size: 14px;
  line-height: 1;
  margin-top: 4px;
  text-decoration: line-through;
`;

const Weight = styled.div<{ $color?: string | null }>`
  color: ${({ $color, theme }) => $color ?? (theme?.isBackgroundDark ? '#c7ccd1' : '#646d72')};
  font-size: 13px;
  line-height: 1;
  margin-top: 4px;
`;

/** Curved, none, and checkerBoard all leave the button its default circle, so they get no clip path. */
export function isCircularButtonFlair(flair: MobileAppFlair | undefined): boolean {
  return (
    !flair || flair === MobileAppFlair.none || flair === MobileAppFlair.curved || flair === MobileAppFlair.checkerBoard
  );
}

const tintedButtonStyles = css`
  /* Hex alpha 10% */
  background: ${({ theme }) => theme.linkColor}1A;
  color: ${({ theme }) => TinyColor.mix(theme.linkColor, '#000', 20).toString()};
`;

const solidButtonStyles = css`
  background: ${({ theme }) => theme.linkColor};
  color: ${({ theme }) => getContrastTextColor(theme?.linkColor)};
`;

// Mobile outline style is the text color, not theme-dependent
const borderButtonStyles = css`
  background: transparent;
  border: 1px solid ${({ theme }) => (theme?.isBackgroundDark ? '#ffffff' : '#121516')};
  color: ${({ theme }) => (theme?.isBackgroundDark ? '#ffffff' : '#121516')};
`;

const LargeAddButton = styled.button<{ $variant: ProductCardButtonOption }>`
  appearance: none;
  border: none;
  border-radius: ${({ theme }) => theme?.radius?.buttons};
  cursor: pointer;
  font: 600 16px / 1 ${({ theme }) => theme?.secondaryFont};
  margin-top: 12px;
  min-height: 40px;
  padding: 10px 16px;
  text-transform: ${({ theme }) => theme?.textTransforms?.buttons};
  width: 100%;

  ${({ $variant }) => {
    if ($variant === ProductCardButtonOption.tinted) {
      return tintedButtonStyles;
    }

    if ($variant === ProductCardButtonOption.border) {
      return borderButtonStyles;
    }

    return solidButtonStyles;
  }}
`;

// Shares the Button Decoration setting with the app's other buttons: the circular default becomes
// a diamond, starburst or blob via clip-path, so the radius only applies to the un-clipped shapes.
export const AddButton = styled.button<{ $buttonFlair?: MobileAppFlair; $color?: string }>`
  align-items: center;
  appearance: none;
  background: ${({ $color, theme }) => $color ?? theme?.linkColor};
  border: none;
  border-radius: ${({ $buttonFlair }) => (isCircularButtonFlair($buttonFlair) ? '50%' : '0')};
  clip-path: ${({ $buttonFlair }) => getButtonClipPath($buttonFlair) || 'none'};
  color: #fff;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 36px;
  justify-content: center;
  width: 36px;

  svg {
    fill: currentColor;
    height: 14px;
    width: 14px;
  }
`;
